import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import {
  charterValueIcon,
  charterValueOrdinal,
} from "@/lib/charter-value-icons"
import type { InviteValueCard } from "@/types/invites"

/**
 * OKS-383 — "What {Brand} stands for" — the three brand value cards on
 * the `valid` invite landing (UX Spec §2.1).
 *
 * Cards arrive resolved-with-fallback from the server (authored strings,
 * or platform defaults localized in the brand's locale), so this
 * component never decides content — it only renders. Rendered as a
 * semantic `<ul>` list (UX Spec §6 a11y); the icon badge is decorative
 * (the title carries meaning). When a card's `icon_key` is null/unknown
 * the badge falls back to the ordinal "01/02/03".
 */
export interface InviteValueCardsProps {
  brandName: string
  cards: InviteValueCard[]
  testId?: string
}

export function InviteValueCards({
  brandName,
  cards,
  testId = "invite-value-cards",
}: InviteValueCardsProps) {
  const { t } = useTranslation()

  if (cards.length === 0) return null

  return (
    <section data-testid={testId} className="flex flex-col gap-3">
      <h2 className="text-foreground text-sm font-semibold tracking-wide">
        {t("invites.landing.values.heading", { brand: brandName })}
      </h2>
      <ul className="flex flex-col gap-3">
        {cards.map((card, index) => {
          const Icon = charterValueIcon(card.icon_key)
          return (
            <li key={index}>
              <Card
                data-testid={`${testId}-card`}
                className="flex flex-row items-start gap-3 p-4"
              >
                <span
                  aria-hidden="true"
                  data-testid={`${testId}-icon`}
                  className="bg-foreground/5 text-foreground flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                >
                  {Icon ? (
                    <Icon className="size-5" />
                  ) : (
                    charterValueOrdinal(index)
                  )}
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="text-foreground truncate text-sm font-semibold">
                    {card.title}
                  </p>
                  <p className="text-muted-foreground line-clamp-2 text-sm">
                    {card.body}
                  </p>
                </div>
              </Card>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default InviteValueCards
